import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { Dialog, IconButton, Badge } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CollectionsIcon from "@mui/icons-material/Collections";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import SoftBox from "../SoftBox";
import SoftTypography from "../SoftTypography";
import { shrinkImageForUpload, getImageGps } from "../../helpers/imageOrientation";
import { reverseGeocodeAddress } from "../../helpers/geocode";
import { useKpiSubmit } from "../../hooks/kpi";

// Photos are saved at up to 1600px (server caps the same); 1200px at 0.8 looked soft.
const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.85;
const TOAST_DURATION = 3000;
const ADDRESS_GRACE_MS = 1500;
const CONFETTI_COUNT = 40;
const CONFETTI_COLORS = ["#7C3AED", "#6ee7b7", "#fbbf24", "#f472b6", "#38bdf8", "#fff"];

const QuickCapture = ({ kpi, userId, companyId, open, onClose, onSave }) => {
  const captureMode = kpi?.requiredInputs?.find((i) => i.type === "quick-capture")?.captureMode || "single";
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const locationRef = useRef(null);
  const addressRef = useRef("Acquiring location...");
  const watchIdRef = useRef(null);
  const geocodeTimerRef = useRef(null);
  const lastGeocodeRef = useRef(0);
  const canvasRef = useRef(document.createElement("canvas"));
  const fileInputRef = useRef(null);
  // galleryBusyRef: a picked photo is being shrunk/uploaded. openRef: lets the
  // async gallery flow see whether the dialog was closed meanwhile, so it never
  // restarts the camera behind a closed dialog.
  const galleryBusyRef = useRef(false);
  const openRef = useRef(open);
  openRef.current = open;
  const toastTimerRef = useRef(null);
  const autoCloseTimerRef = useRef(null);

  const [captureCount, setCaptureCount] = useState(0);
  const [capturing, setCapturing] = useState(false);
  const [displayAddress, setDisplayAddress] = useState("Acquiring location...");
  const [cameraReady, setCameraReady] = useState(false);
  const [locationReady, setLocationReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [captureToast, setCaptureToast] = useState(null);
  const [showFlash, setShowFlash] = useState(false);
  const [confettiKey, setConfettiKey] = useState(0);
  const [captures, setCaptures] = useState([]);
  const [previewCapture, setPreviewCapture] = useState(null);
  const [previewNote, setPreviewNote] = useState("");
  const [noteSaved, setNoteSaved] = useState(false);
  const [closing, setClosing] = useState(false);

  const confettiParticles = useMemo(() => {
    if (confettiKey === 0) return [];
    return Array.from({ length: CONFETTI_COUNT }, (_, i) => {
      const size = 6 + Math.random() * 6;
      return {
        id: `${confettiKey}-${i}`,
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        left: `${Math.random() * 100}%`,
        delay: `${Math.random() * 0.3}s`,
        dur: `${0.8 + Math.random() * 0.6}s`,
        size,
        heightRatio: 0.6 + Math.random() * 0.8,
        drift: -40 + Math.random() * 80,
        rot: Math.random() * 720,
        translateY: 120 + Math.random() * 200,
        isCircle: Math.random() > 0.5,
      };
    });
  }, [confettiKey]);

  useEffect(() => {
    if (confettiKey > 0) {
      const timer = setTimeout(() => setConfettiKey(0), 2000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [confettiKey]);

  const { submitKpi } = useKpiSubmit();

  const startCamera = useCallback(async () => {
    const initStream = (stream) => {
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play();
          setCameraReady(true);
        };
      }
    };

    try {
      initStream(await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1920 }, height: { ideal: 1440 } },
        audio: false,
      }));
    } catch {
      try {
        initStream(await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1920 }, height: { ideal: 1440 } },
          audio: false,
        }));
      } catch {
        try {
          initStream(await navigator.mediaDevices.getUserMedia({ video: true, audio: false }));
        } catch {
          setCameraError("No camera available. Use the gallery button to upload a photo.");
        }
      }
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraReady(false);
  }, []);

  const throttledGeocode = useCallback(async (lat, lng) => {
    const now = Date.now();
    if (now - lastGeocodeRef.current < 5000) return;
    lastGeocodeRef.current = now;
    try {
      const geo = await reverseGeocodeAddress(lat, lng);
      addressRef.current = geo.address;
      setDisplayAddress(geo.address);
    } catch {
      /* keep previous address */
    }
  }, []);

  const startLocationWatch = useCallback(() => {
    if (!navigator.geolocation) {
      addressRef.current = "Geolocation not supported";
      setDisplayAddress("Geolocation not supported");
      return;
    }

    const onPosition = (position) => {
      const { latitude: lat, longitude: lng } = position.coords;
      locationRef.current = { lat, lng };
      setLocationReady(true);
      throttledGeocode(lat, lng);
    };

    const onError = () => {
      if (!locationRef.current) {
        addressRef.current = "Location unavailable";
        setDisplayAddress("Location unavailable");
      }
    };

    navigator.geolocation.getCurrentPosition(onPosition, onError, {
      enableHighAccuracy: false,
      maximumAge: 30000,
      timeout: 10000,
    });

    watchIdRef.current = navigator.geolocation.watchPosition(
      onPosition,
      onError,
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 },
    );
  }, [throttledGeocode]);

  const stopLocationWatch = useCallback(() => {
    if (watchIdRef.current != null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (geocodeTimerRef.current) {
      clearTimeout(geocodeTimerRef.current);
      geocodeTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (open) {
      setCaptureCount(0);
      setCameraError(null);
      setCaptureToast(null);
      setCaptures([]);
      setPreviewCapture(null);
      setLocationReady(false);
      addressRef.current = "Acquiring location...";
      setDisplayAddress("Acquiring location...");
      startCamera();
      startLocationWatch();
    }
    return () => {
      stopCamera();
      stopLocationWatch();
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, [open, startCamera, stopCamera, startLocationWatch, stopLocationWatch]);

  const showCaptureToast = useCallback((thumb, address, status, message, entry) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setCaptureToast({ thumb, address, status, message, fading: false, entry });
    // "Uploading..." stays up until the save answers; it used to fade after 3s
    // and leave the player looking at nothing while the upload went on.
    if (status === "uploading") return;
    toastTimerRef.current = setTimeout(() => {
      setCaptureToast((prev) => (prev ? { ...prev, fading: true } : null));
      toastTimerRef.current = setTimeout(() => {
        setCaptureToast(null);
      }, 500);
    }, TOAST_DURATION);
  }, []);

  const handleClose = useCallback(() => {
    setClosing(true);
    setConfettiKey(0);
    stopCamera();
    stopLocationWatch();
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
    autoCloseTimerRef.current = null;
    setTimeout(() => {
      setClosing(false);
      onClose?.();
    }, 50);
  }, [stopCamera, stopLocationWatch, onClose]);

  const handleCapture = useCallback(async () => {
    const video = videoRef.current;
    if (!video || !cameraReady || capturing) return;

    setCapturing(true);
    setShowFlash(true);
    setTimeout(() => setShowFlash(false), 200);

    try {
      const canvas = canvasRef.current;
      const vw = video.videoWidth;
      const vh = video.videoHeight;

      let dw = vw;
      let dh = vh;
      if (Math.max(vw, vh) > MAX_DIMENSION) {
        const ratio = MAX_DIMENSION / Math.max(vw, vh);
        dw = Math.round(vw * ratio);
        dh = Math.round(vh * ratio);
      }

      canvas.width = dw;
      canvas.height = dh;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, dw, dh);

      const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY);
      });

      if (!blob) {
        showCaptureToast(null, "", "error", "Failed to capture image");
        setCapturing(false);
        return;
      }

      const thumbUrl = URL.createObjectURL(blob);

      const loc = locationRef.current;
      let address = "";
      let lat = null;
      let lng = null;

      if (loc) {
        lat = loc.lat;
        lng = loc.lng;
        if (addressRef.current && !addressRef.current.startsWith("Acquiring")) {
          address = addressRef.current;
        }
      }

      showCaptureToast(thumbUrl, address || "No address yet", "uploading", "Uploading...");

      const file = new File(
        [blob],
        `quick-capture-${Date.now()}.jpg`,
        { type: "image/jpeg" },
      );

      const formData = new FormData();
      formData.append("image", file);
      formData.append("imageKey", "photo");
      formData.append("amount", 1);
      // Real save time — the server pins evening entries to noon (see YardSignPost).
      formData.append("takenAt", new Date().toISOString());
      if (address) formData.append("address", address);
      if (lat != null) formData.append("lat", lat);
      if (lng != null) formData.append("lng", lng);

      await submitKpi(kpi._id, userId, null, formData, companyId);

      const newCount = captureCount + 1;
      const captureEntry = {
        id: `cap-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        thumb: thumbUrl,
        address,
        number: newCount,
        time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        note: "",
      };
      setCaptures((prev) => [captureEntry, ...prev]);
      setCaptureCount(newCount);
      setConfettiKey((k) => k + 1);
      showCaptureToast(thumbUrl, address, "done", `Saved (#${newCount})`, captureEntry);
      onSave?.();
      if (captureMode === "single") {
        if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = setTimeout(() => handleClose(), 3000);
      }
    } catch (err) {
      showCaptureToast(null, "", "error", err?.message || "Capture failed");
    } finally {
      setCapturing(false);
    }
  }, [
    cameraReady,
    capturing,
    captureCount,
    captureMode,
    kpi,
    userId,
    companyId,
    onSave,
    showCaptureToast,
    submitKpi,
    handleClose,
  ]);

  const handleGalleryUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file || capturing) return;
    e.target.value = null;

    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
    galleryBusyRef.current = true;
    setCapturing(true);
    let keepCameraOff = false;
    try {
      // No thumbnail yet: showing the phone's original (12+ MP) here meant iOS
      // decoded it for the toast while also decoding it to shrink, which on
      // top of the camera stream ran Safari out of memory (white screen).
      showCaptureToast(null, "Preparing photo...", "uploading", "Uploading...");

      // GPS comes from the original file's EXIF (the shrunk copy has none), and
      // the address lookup runs alongside the shrink instead of before it.
      const gpsPromise = getImageGps(file)
        .then((gps) => (gps?.latitude != null && gps?.longitude != null ? gps : null))
        .catch(() => null); /* no EXIF data or parsing failed – keep address blank */
      const addressPromise = gpsPromise.then((gps) => (gps
        ? reverseGeocodeAddress(gps.latitude, gps.longitude)
          .then((geo) => geo?.address || "")
          .catch(() => "")
        : ""));

      const [photo, gps] = await Promise.all([
        shrinkImageForUpload(file, MAX_DIMENSION, JPEG_QUALITY),
        gpsPromise,
      ]);
      const lat = gps?.latitude ?? null;
      const lng = gps?.longitude ?? null;
      // The street lookup (free Nominatim) can take seconds from the field.
      // Give it ADDRESS_GRACE_MS once the photo is ready, then send the
      // coordinates as the place rather than holding the upload for it.
      const address = lat == null ? "" : await Promise.race([
        addressPromise,
        new Promise((resolve) => {
          setTimeout(() => resolve(`${lat.toFixed(6)}, ${lng.toFixed(6)}`), ADDRESS_GRACE_MS);
        }),
      ]);

      // Thumbnails (toast, captures strip, preview) use the shrunk copy.
      const thumbUrl = URL.createObjectURL(photo);
      showCaptureToast(thumbUrl, address || "No address", "uploading", "Uploading...");

      const formData = new FormData();
      formData.append("image", photo);
      formData.append("imageKey", "photo");
      formData.append("amount", 1);
      // Real save time — the server pins evening entries to noon (see YardSignPost).
      formData.append("takenAt", new Date().toISOString());
      if (address) formData.append("address", address);
      if (lat != null) formData.append("lat", lat);
      if (lng != null) formData.append("lng", lng);

      await submitKpi(kpi._id, userId, null, formData, companyId);

      const newCount = captureCount + 1;
      const captureEntry = {
        id: `cap-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        thumb: thumbUrl,
        address,
        number: newCount,
        time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        note: "",
      };
      setCaptures((prev) => [captureEntry, ...prev]);
      setCaptureCount(newCount);
      setConfettiKey((k) => k + 1);
      showCaptureToast(thumbUrl, address, "done", `Saved (#${newCount})`, captureEntry);
      onSave?.();
      if (captureMode === "single") {
        keepCameraOff = true;
        if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = setTimeout(() => handleClose(), 3000);
      }
    } catch (err) {
      showCaptureToast(null, "", "error", err?.message || "Upload failed");
    } finally {
      galleryBusyRef.current = false;
      setCapturing(false);
      // The camera was paused when the picker opened; bring it back unless the
      // dialog is about to close on its own.
      if (!keepCameraOff && openRef.current && !streamRef.current) startCamera();
    }
  }, [capturing, captureCount, captureMode, kpi, userId, companyId, onSave, showCaptureToast, submitKpi, handleClose, startCamera]);

  // The live camera stream is the biggest memory user on the page. Pause it
  // while the photo picker is open and the picked photo is being shrunk, so
  // iOS Safari has room to decode a full-size photo without killing the page.
  const openGalleryPicker = useCallback(() => {
    stopCamera();
    // If the picker is dismissed without a photo, restart the camera. The
    // input's "cancel" event covers iOS 16.4+/Chrome 113+; the focus check
    // covers older browsers (the page regains focus when the picker closes).
    const onFocus = () => {
      window.removeEventListener("focus", onFocus);
      setTimeout(() => {
        if (openRef.current && !galleryBusyRef.current && !streamRef.current) startCamera();
      }, 1000);
    };
    window.addEventListener("focus", onFocus);
    fileInputRef.current?.click();
  }, [stopCamera, startCamera]);

  // React has no onCancel for <input>, so listen for the native event.
  useEffect(() => {
    const input = fileInputRef.current;
    if (!open || !input) return undefined;
    const onCancel = () => {
      if (openRef.current && !galleryBusyRef.current && !streamRef.current) startCamera();
    };
    input.addEventListener("cancel", onCancel);
    return () => input.removeEventListener("cancel", onCancel);
  }, [open, startCamera]);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: "#000",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <SoftBox
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          bgcolor: "rgba(0,0,0,0.7)",
          position: "relative",
          zIndex: 2,
        }}
      >
        <SoftBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Badge
            badgeContent={captureCount}
            color="primary"
            showZero
            max={999}
            sx={{
              "& .MuiBadge-badge": {
                fontSize: "0.85rem",
                fontWeight: 700,
                minWidth: "1.5rem",
                height: "1.5rem",
              },
            }}
          >
            <CameraAltIcon sx={{ color: "#fff", fontSize: "1.5rem" }} />
          </Badge>
          <SoftTypography
            variant="body2"
            sx={{ color: "#fff", fontWeight: 600, ml: 0.5 }}
          >
            {kpi.title ?? "Live Camera"}
          </SoftTypography>
        </SoftBox>
        <SoftBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {captures.length > 0 && (
            <IconButton
              onClick={() => {
                setPreviewCapture(captures[0]);
                setPreviewNote(captures[0].note || "");
                setNoteSaved(false);
              }}
              sx={{ color: "#fff" }}
            >
              <Badge badgeContent={captures.length} color="success" max={99}>
                <CollectionsIcon sx={{ fontSize: "1.3rem" }} />
              </Badge>
            </IconButton>
          )}
          <IconButton onClick={handleClose} sx={{ color: "#fff" }}>
            <CloseRoundedIcon />
          </IconButton>
        </SoftBox>
      </SoftBox>

      {/* Camera viewfinder */}
      <SoftBox
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {cameraError ? (
          <SoftTypography
            sx={{ color: "#ff6b6b", textAlign: "center", px: 3 }}
          >
            {cameraError}
          </SoftTypography>
        ) : (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        )}

        {showFlash && (
          <SoftBox
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "#fff",
              zIndex: 10,
              animation: "flashFade 0.3s ease-out forwards",
              "@keyframes flashFade": {
                "0%": { opacity: 0.85 },
                "100%": { opacity: 0 },
              },
            }}
          />
        )}

        {capturing && !showFlash && (
          <SoftBox
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(0,0,0,0.35)",
              zIndex: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SoftBox
              sx={{
                width: 48,
                height: 48,
                border: "4px solid rgba(255,255,255,0.2)",
                borderTop: "4px solid #fff",
                borderRadius: "50%",
                animation: "spin 0.7s linear infinite",
                "@keyframes spin": {
                  from: { transform: "rotate(0deg)" },
                  to: { transform: "rotate(360deg)" },
                },
              }}
            />
          </SoftBox>
        )}

        {confettiParticles.length > 0 && !closing && (
          <SoftBox
            key={confettiKey}
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 12,
              pointerEvents: "none",
              overflow: "hidden",
            }}
          >
            {confettiParticles.map((p) => (
              <SoftBox
                key={p.id}
                sx={{
                  position: "absolute",
                  top: "40%",
                  left: p.left,
                  width: p.size,
                  height: p.size * p.heightRatio,
                  bgcolor: p.color,
                  borderRadius: p.isCircle ? "50%" : "2px",
                  opacity: 0,
                  animation: `confettiBurst ${p.dur} ${p.delay} ease-out forwards`,
                  "@keyframes confettiBurst": {
                    "0%": { opacity: 1, transform: "translateY(0) translateX(0) rotate(0deg) scale(1)" },
                    "100%": { opacity: 0, transform: `translateY(-${p.translateY}px) translateX(${p.drift}px) rotate(${p.rot}deg) scale(0.3)` },
                  },
                }}
              />
            ))}
          </SoftBox>
        )}

        {/* Capture toast overlay */}
        {captureToast && !closing && (
          <SoftBox
            sx={{
              position: "absolute",
              left: 12,
              right: 12,
              bottom: 16,
              zIndex: 5,
              bgcolor: "rgba(0,0,0,0.6)",
              backdropFilter: "blur(12px)",
              borderRadius: "16px",
              p: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              transition: "opacity 0.5s ease, transform 0.5s ease",
              opacity: captureToast.fading ? 0 : 1,
              transform: captureToast.fading ? "translateY(-20px)" : "translateY(0)",
            }}
          >
            {captureToast.thumb && (
              <SoftBox
                component="img"
                src={captureToast.thumb}
                onClick={() => {
                  if (captureToast.entry) {
                    setPreviewCapture(captureToast.entry);
                    setPreviewNote(captureToast.entry.note || "");
                    setNoteSaved(false);
                  }
                }}
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "12px",
                  objectFit: "cover",
                  flexShrink: 0,
                  border: "1px solid rgba(255,255,255,0.2)",
                  cursor: captureToast.entry ? "pointer" : "default",
                }}
              />
            )}
            <SoftBox sx={{ flex: 1, minWidth: 0 }}>
              <SoftBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {captureToast.status === "uploading" && (
                  <SoftBox
                    sx={{
                      width: 14,
                      height: 14,
                      border: "2px solid rgba(255,255,255,0.3)",
                      borderTop: "2px solid #fff",
                      borderRadius: "50%",
                      flexShrink: 0,
                      animation: "spin 0.8s linear infinite",
                      "@keyframes spin": {
                        from: { transform: "rotate(0deg)" },
                        to: { transform: "rotate(360deg)" },
                      },
                    }}
                  />
                )}
                {captureToast.status === "done" && (
                  <CheckCircleIcon sx={{ fontSize: 16, color: "#6ee7b7", flexShrink: 0 }} />
                )}
                {captureToast.status === "error" && (
                  <ErrorOutlineIcon sx={{ fontSize: 16, color: "#fca5a5", flexShrink: 0 }} />
                )}
                <SoftTypography
                  variant="caption"
                  sx={{
                    color: "rgba(255,255,255,0.5)",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    fontSize: "0.65rem",
                  }}
                >
                  {(() => { if (captureToast.status === "uploading") return "Uploading..."; if (captureToast.status === "done") return "Saved"; return "Error"; })()}
                </SoftTypography>
              </SoftBox>
              <SoftTypography
                variant="body2"
                sx={{
                  color: "rgba(255,255,255,0.9)",
                  fontWeight: 500,
                  fontSize: "0.8rem",
                  mt: 0.25,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {captureToast.address || captureToast.message}
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        )}
      </SoftBox>

      {/* Footer with address and shutter */}
      <SoftBox
        sx={{
          bgcolor: "rgba(0,0,0,0.85)",
          px: 2,
          py: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
          position: "relative",
          zIndex: 2,
        }}
      >
        {/* Live address display */}
        <SoftBox
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            maxWidth: "100%",
          }}
        >
          <LocationOnIcon
            sx={{ color: "rgba(255,255,255,0.6)", fontSize: "1rem", flexShrink: 0 }}
          />
          <SoftTypography
            variant="caption"
            sx={{
              color: "rgba(255,255,255,0.6)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "80vw",
            }}
          >
            {displayAddress}
          </SoftTypography>
        </SoftBox>

        {/* Shutter row: gallery button | shutter | spacer */}
        <SoftBox sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", position: "relative" }}>
          {/* Gallery upload button - left side */}
          <SoftBox sx={{ position: "absolute", left: 16 }}>
            <IconButton
              onClick={openGalleryPicker}
              disabled={capturing || (captureMode === "single" && captureCount > 0)}
              sx={{
                width: 48,
                height: 48,
                bgcolor: "rgba(255,255,255,0.12)",
                color: "#fff",
                "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                "&.Mui-disabled": { color: "rgba(255,255,255,0.2)" },
              }}
            >
              <PhotoLibraryIcon sx={{ fontSize: "1.5rem" }} />
            </IconButton>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              onChange={handleGalleryUpload}
            />
          </SoftBox>

          {/* Shutter button - center */}
          <SoftBox sx={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            {!locationReady && cameraReady && (
              <SoftBox
                sx={{
                  position: "absolute",
                  width: 84,
                  height: 84,
                  borderRadius: "50%",
                  border: "3px solid transparent",
                  borderTopColor: "#7C3AED",
                  animation: "spin 1s linear infinite",
                  "@keyframes spin": {
                    from: { transform: "rotate(0deg)" },
                    to: { transform: "rotate(360deg)" },
                  },
                }}
              />
            )}
            <IconButton
              onClick={handleCapture}
              disabled={!cameraReady || !locationReady || capturing || (captureMode === "single" && captureCount > 0)}
              sx={{
                width: 72,
                height: 72,
                border: "4px solid #fff",
                bgcolor: capturing ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.1)",
                transition: "all 0.15s ease",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.25)",
                },
                "&:active": {
                  transform: "scale(0.92)",
                  bgcolor: "rgba(255,255,255,0.4)",
                },
                "&.Mui-disabled": {
                  border: "4px solid rgba(255,255,255,0.15)",
                },
              }}
            >
              <SoftBox
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  bgcolor: "#fff",
                  opacity: (() => { if (!locationReady) return 0.2; if (capturing) return 0.5; return 1; })(),
                  transition: "opacity 0.15s ease",
                }}
              />
            </IconButton>
          </SoftBox>
        </SoftBox>
        {!locationReady && cameraReady && (
          <SoftTypography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.7rem" }}>
            Waiting for GPS location...
          </SoftTypography>
        )}

        {captureCount > 0 && locationReady && (
          <SoftTypography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>
            {captureCount} {captureCount === 1 ? "capture" : "captures"} this session
          </SoftTypography>
        )}
      </SoftBox>

      {previewCapture && (
        <Dialog
          open={!!previewCapture}
          onClose={() => {
            if (previewNote !== (previewCapture.note || "")) {
              setCaptures((prev) =>
                prev.map((c) => (c.id === previewCapture.id ? { ...c, note: previewNote } : c)),
              );
            }
            setPreviewCapture(null);
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: "#111",
              borderRadius: "20px",
              overflow: "hidden",
              m: 2,
              maxHeight: "90vh",
            },
          }}
        >
          <SoftBox sx={{ position: "relative" }}>
            <SoftBox
              component="img"
              src={previewCapture.thumb}
              sx={{
                width: "100%",
                maxHeight: "50vh",
                objectFit: "contain",
                bgcolor: "#000",
                display: "block",
              }}
            />
            <IconButton
              onClick={() => {
                if (previewNote !== (previewCapture.note || "")) {
                  setCaptures((prev) =>
                    prev.map((c) => (c.id === previewCapture.id ? { ...c, note: previewNote } : c)),
                  );
                }
                setPreviewCapture(null);
              }}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                bgcolor: "rgba(0,0,0,0.5)",
                color: "#fff",
                "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
              }}
            >
              <CloseRoundedIcon />
            </IconButton>
            <SoftBox
              sx={{
                position: "absolute",
                top: 8,
                left: 8,
                bgcolor: "rgba(0,0,0,0.5)",
                borderRadius: "10px",
                px: 1.5,
                py: 0.5,
              }}
            >
              <SoftTypography variant="caption" sx={{ color: "#fff", fontWeight: 700 }}>
                #{previewCapture.number}
              </SoftTypography>
            </SoftBox>
          </SoftBox>

          <SoftBox sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
            <SoftBox sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <SoftBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <LocationOnIcon sx={{ fontSize: "1rem", color: "rgba(255,255,255,0.5)" }} />
                <SoftTypography
                  variant="caption"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    maxWidth: "60vw",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {previewCapture.address || "No address"}
                </SoftTypography>
              </SoftBox>
              <SoftTypography variant="caption" sx={{ color: "rgba(255,255,255,0.4)" }}>
                {previewCapture.time}
              </SoftTypography>
            </SoftBox>


            {captures.length > 1 && (
              <SoftBox sx={{ display: "flex", gap: 1, overflowX: "auto", pt: 1, pb: 0.5 }}>
                {captures.map((cap) => (
                  <SoftBox
                    key={cap.id}
                    component="img"
                    src={cap.thumb}
                    onClick={() => {
                      if (previewNote !== (previewCapture.note || "")) {
                        setCaptures((prev) =>
                          prev.map((c) => (c.id === previewCapture.id ? { ...c, note: previewNote } : c)),
                        );
                      }
                      setPreviewCapture(cap);
                      setPreviewNote(cap.note || "");
                      setNoteSaved(false);
                    }}
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: "10px",
                      objectFit: "cover",
                      flexShrink: 0,
                      cursor: "pointer",
                      border: cap.id === previewCapture.id
                        ? "2px solid #7C3AED"
                        : "2px solid transparent",
                      opacity: cap.id === previewCapture.id ? 1 : 0.6,
                      transition: "all 0.15s ease",
                      "&:hover": { opacity: 1 },
                    }}
                  />
                ))}
              </SoftBox>
            )}
          </SoftBox>
        </Dialog>
      )}
    </Dialog>
  );
};

export default QuickCapture;
